import { defineComponent, h, ref, watch } from 'vue';
import { Modal } from '@/ui/components/Modal';
import { loader, pageSubtitle } from '@/ui/components';
import { t } from '@/ui/i18n/i18n';
import { SelectOptions } from '@/ui/types';
import { useTask } from '@/ui/concerns';
import {
  VDataComponents,
  sequence,
  filterListToOptions,
  filterListToEditableGroups,
  filterListToEditableUsers,
} from '@/ui/helpers';
import { filter as getAllUsers } from '@/services/api/resources/user';
import { Button } from '@/ui/components/Button';
import cloneDeep from 'lodash/cloneDeep';
import { emptyChecklistTemplateBuilder, ChecklistTemplateBuilderEditable } from '@/ui/DTO/checklistTemplateBuilderEditable';
import { useChecklistTemplateBuilder } from '@/ui/components/checklist/useChecklistTemplateBuilder';
import { filter as getAllGroups } from '@/services/api/resources/group';
import { filter as getAllChecklists } from '@/services/api/resources/checklist';
import { CREATE_ACTION_MODAL_WIDTH } from '@/ui/components/modals/constants';
import { useAssignees } from '@/ui/concerns/useAssignees';

interface ChecklistTemplateCreateModalProps {
  inspectionAction?: ChecklistTemplateBuilderEditable | null;
}

export const InspectionActionCreatorModal = defineComponent<ChecklistTemplateCreateModalProps>({
  name: 'InspectionActionCreatorModal',
  props: ['active', 'inspectionAction'],
  setup(props, context) {
    const checklists = ref<SelectOptions>([]);
    const checklistEditable = ref(props.inspectionAction ? cloneDeep(props.inspectionAction) : emptyChecklistTemplateBuilder());

    const {
      formFields,
      showValidationErrors,
      hasNoValidationErrors,
    } = useChecklistTemplateBuilder(checklistEditable);

    const closeModal = () => {
      checklistEditable.value = emptyChecklistTemplateBuilder();
      context.emit('close');
    };

    const emitSave = () => {
      checklistEditable.value.assignees.value = filterListToEditableUsers(checklistEditable.value.assignees.value);
      checklistEditable.value.assignedGroups.value = filterListToEditableGroups(checklistEditable.value.assignedGroups.value);

      context.emit('save', cloneDeep(checklistEditable.value));

      checklistEditable.value = emptyChecklistTemplateBuilder();
      showValidationErrors.value = false;
    };

    const onSaveClick = () => {
      showValidationErrors.value = true;

      if (hasNoValidationErrors.value) {
        emitSave();
      }
    };

    const {
      assigneeOptions,
      fillAssigneeOptions,
      fillGroupsOptions,
      getUsers,
    } = useAssignees();

    // get record
    const getFilters = useTask(
      () => sequence(getAllUsers(), getAllGroups(), getAllChecklists()),
      ([users, groups, checklistList]) => {
        fillGroupsOptions(groups);
        fillAssigneeOptions(users);
        checklists.value = filterListToOptions(checklistList);
      },
    );

    const onChecklistTemplatePropChange = (resource: ChecklistTemplateBuilderEditable | null | undefined) => {
      checklistEditable.value = cloneDeep(resource) || emptyChecklistTemplateBuilder();
    };

    watch(() => props.inspectionAction, onChecklistTemplatePropChange);

    return function render() {
      return h(Modal, {
        props: {
          ...props,
          active: true,
          width: CREATE_ACTION_MODAL_WIDTH,
          ariaRole: 'dialog',
          trapFocus: true,
          destroyOnHide: false,
          ariaModal: true,
        },
        on: {
          close: closeModal,
        },
        scopedSlots: {
          actions: () => h('div', [
            h(Button, {
              props: { type: 'is-text', btnClass: 'mr-2 has-text-weight-semibold' },
              on: { click: closeModal },
            }, t('modal.cancel')),
            h(Button, {
              props: {
                loading: false,
                disabled: false,
              },
              on: { click: onSaveClick },
            }, t('common.save')),
          ]),
        },
      }, [
        loader('div', getFilters, [
          pageSubtitle(t('checklist-template.inspection')),
          ...formFields(assigneeOptions.value, checklists.value, getUsers),
        ], true, 'inspection-creator-modal'),
      ]);
    };
  },
});

export function inspectionActionCreatorModal(data: VDataComponents<ChecklistTemplateCreateModalProps>) {
  return h(InspectionActionCreatorModal, data);
}
